import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';

import CopyCalendarOption from './CopyCalendarOption';
import { buttonReset, focusRing } from '../../../../shared/theme/focusStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const ExpandButton = styled('button')`
  ${buttonReset}
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  cursor: pointer;
  transition: transform 0.2s ease;
  ${focusRing}
`;

const ExpandSpacer = styled('span')`
  flex-shrink: 0;
  width: 20px;
`;

const CopyCalendarPickerRow = ({ calendar, checkedCalendars, onToggleCalendar }) => {
  const [isOpen, setIsOpen] = useState(true);
  const { colors } = useTheme();

  const hasChildren = calendar.childCalendars.length > 0;
  const hasCheckedChild = calendar.childCalendars
    .some((child) => checkedCalendars.has(child.id));

  const handleExpandClick = () => {
    setIsOpen((open) => !open);
  };

  useEffect(() => {
    if (hasCheckedChild && !isOpen) {
      setIsOpen(true);
    }
    // isOpen is left out on purpose: only a newly checked child should re-open the row.
  }, [hasCheckedChild]);

  return (
    <div>
      <CopyCalendarOption
        calendar={calendar}
        isChecked={checkedCalendars.has(calendar.id)}
        leading={hasChildren ? (
          <ExpandButton
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} sub-calendars of ${calendar.name}`}
            isOpen={isOpen}
            onClick={handleExpandClick}
            type='button'
          >
            <IcomoonReact aria-hidden='true' color={colors.textSecondary} icon='down-arrow' iconSet={iconSet} size={10} />
          </ExpandButton>
        ) : <ExpandSpacer />}
        onToggle={onToggleCalendar}
      />
      {isOpen && calendar.childCalendars.map((child) => (
        <CopyCalendarOption
          calendar={child}
          indented
          isChecked={checkedCalendars.has(child.id)}
          key={child.id}
          onToggle={onToggleCalendar}
        />
      ))}
    </div>
  );
};

CopyCalendarPickerRow.propTypes = {
  calendar: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    childCalendars: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  checkedCalendars: PropTypes.instanceOf(Set).isRequired,
  onToggleCalendar: PropTypes.func.isRequired,
};

export default CopyCalendarPickerRow;
